import { FC } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { cn } from '@/utils/tailwind-utils';
import { useAppDispatch } from '@/hooks/redux';
import { logout } from '@/store/authSlice';
import { LogoLoader } from '@/components/ui/atoms/logo-loader';
import { Avatar, AvatarFallback } from '@/components/ui/atoms/avatar';
import { User, LogOut, ChevronDown } from 'lucide-react';
import useAdminNavigation from '@/hooks/useAdminNavigation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/molecules/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/atoms/tooltip';



export const AdminLayout: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/signup');
  };

  const { navigationSections, navigateTo } = useAdminNavigation();

  return (
    <div className={cn(
      "flex",
      "bg-white"
    )}>
      {/* Left Sidebar */}
      <aside className="w-64 border-r border-gray-200 flex flex-col h-full">
        {/* Logo */}
        <div className="h-16 border-b border-gray-200 flex items-center px-4">
          <LogoLoader size={32} />
          <span className="ml-2 font-semibold text-lg text-violet-700">Maatra Admin</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {Object.entries(navigationSections).map(([key, section]) => {
            if (section.items.length === 0) return null;
            
            return (
              <div key={key} className="mb-6">
                <h3 className="text-xs uppercase text-gray-500 font-semibold px-3 mb-2">{section.title}</h3>
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.label}>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => navigateTo(item.route)}
                              className="flex w-full items-center px-3 py-2 rounded-md hover:bg-violet-50 text-gray-700 hover:text-violet-700 transition-colors text-left"
                            >
                              {item.icon && <item.icon className="w-5 h-5 mr-3" />}
                              <span>{item.label}</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            {item.label}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="h-16 border-b border-gray-200 flex items-center justify-between px-6">
          <h1 className="text-xl font-semibold text-gray-800">Admin Dashboard</h1>
          
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center">
              <Avatar className="h-8 w-8 mr-2">
                <AvatarFallback>
                  <User className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium mr-1">Admin</span>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main 
          className="flex-1 overflow-auto overflow-x-hidden p-6 bg-gray-50"
          style={{ maxHeight: 'calc(var(--vh, 1vh) * 100 - 64px)' }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
